import { useEffect, useMemo, useRef, useState } from "react";
import { createContact, getContact, PLATINUM_AUDIENCE_ID } from "@/services/resend";
import { logger } from "@/utils/logger";
import { triggerUpgradeFlow } from "@/utils/upgrade";
import { Check } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { getStoredProductName } from "@/utils/product";
import { PRODUCT_LEXI_PREMIUM, SHOW_VIP_PROMOS } from "@/utils/vars";

export default function CarouselBanner() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [userEmail, setUserEmail] = useState(null);
  const [userPhone, setUserPhone] = useState(null);
  const [productName, setProductName] = useState(null);
  const [waitlistStatus, setWaitlistStatus] = useState({
    checking: false,
    joining: false,
    PRO: false,
    PLATINUM: false,
    error: null,
  });
  const autoPlayRef = useRef(null);
  const carouselRef = useRef(null);

  // Determine product/plan (used to hide "Upgrade to Lexi Pro" for Lexi Pro users)
  useEffect(() => {
    const refreshProductName = () => {
      try {
        setProductName(getStoredProductName());
      } catch {
        setProductName(null);
      }
    };

    refreshProductName();

    // Keep in sync when other parts of the app persist product changes
    window.addEventListener("lexi:product-updated", refreshProductName);
    window.addEventListener("storage", refreshProductName);

    return () => {
      window.removeEventListener("lexi:product-updated", refreshProductName);
      window.removeEventListener("storage", refreshProductName);
    };
  }, []);

  const isLexiPro =
    (productName || "").toLowerCase() === PRODUCT_LEXI_PREMIUM.toLowerCase();

  // Check if user is signed in and load their details
  useEffect(() => {
    const email = sessionStorage.getItem("userEmail");
    const phone = sessionStorage.getItem("phoneNumber");

    if (email) {
      setUserEmail(email);
      if (phone) {
        setUserPhone(phone);
      }
      // Check waitlist status when user email is available
      if (SHOW_VIP_PROMOS) checkWaitlistStatus(email);
    }
  }, []);

  // Function to check waitlist status for PLATINUM
  const checkWaitlistStatus = async (email) => {
    if (!email) return;

    setWaitlistStatus((prev) => ({ ...prev, checking: true }));

    try {
      const result = await getContact(email);
      if (result.success && result.found && result.data) {
        // Check if contact exists - if they exist, they're on the PLATINUM waitlist
        setWaitlistStatus((prev) => ({ ...prev, PLATINUM: true }));
      }
    } catch (error) {
      logger.error("Error checking waitlist status:", error);
      setWaitlistStatus((prev) => ({
        ...prev,
        error: "Failed to check waitlist status",
      }));
    } finally {
      setWaitlistStatus((prev) => ({ ...prev, checking: false }));
    }
  };

  // Function to handle waitlist signup
  const handleJoinWaitlist = async (tier) => {
    if (!userEmail) {
      // Redirect to sign in if not logged in
      window.location.href = "/single-sso";
      return;
    }

    // Only handle PLATINUM tier with Resend
    if (tier !== "PLATINUM") {
      logger.warn("Only PLATINUM tier is supported with Resend");
      return;
    }

    // Set the tier that's being joined
    setWaitlistStatus((prev) => ({
      ...prev,
      joining: tier,
      error: null,
    }));

    try {
      // Create contact in Resend with PLATINUM audience
      await createContact({
        email: userEmail,
        audienceId: PLATINUM_AUDIENCE_ID,
        phoneNumber: userPhone || undefined,
      });

      // Update status to show as joined
      setWaitlistStatus((prev) => ({
        ...prev,
        [tier]: true,
        joining: false,
      }));
    } catch (error) {
      logger.error(`Error joining ${tier} waitlist:`, error);
      setWaitlistStatus((prev) => ({
        ...prev,
        joining: false,
        error: `Failed to join ${tier} waitlist`,
      }));
    }
  };

  const bannerSlides = useMemo(() => {
    const baseSlides = [
      {
        id: 1,
        title: "Lexi Social Update",
        description: [
          "Now free for life!",
          "Includes top-2 morning tips",
          // "Upgrade for exotics and race-time tips"
        ],
        // cta: "UPGRADE NOW",
        // ctaAction: () => (window.location.href = "/app/dashboard"),
        gradientFrom: "#333333", // rgb(255 20 147)
        gradientTo: "#f30099", // rgb(255 20 147)
      },
      {
        id: 2,
        title: "Upgrade to Lexi Pro",
        description: ["Morning tips and race-time tips", "Includes exotics (4 tips)"],
        cta: "UPGRADE NOW",
        ctaAction: triggerUpgradeFlow,
        // cta: waitlistStatus.PRO ? "JOINED" : "JOIN WAITLIST",
        // ctaAction: () => !waitlistStatus.PRO && handleJoinWaitlist("PRO"),
        // isWaitlistButton: true,
        // waitlistTier: "PRO",
        gradientFrom: "#333333", // purple-700
        gradientTo: "#A855F7", // purple-500
      },
      {
        id: 3,
        title: "Lexi VIP",
        description: ["Everything in Lexi Pro, plus", "Detailed analysis and insights"],
        cta: waitlistStatus.PLATINUM ? "JOINED WAITLIST" : "JOIN WAITLIST",
        ctaAction: () =>
          !waitlistStatus.PLATINUM && handleJoinWaitlist("PLATINUM"),
        isWaitlistButton: true,
        waitlistTier: "PLATINUM",
        gradientFrom: "#BE185D", // pink-700
        gradientTo: "#EC4899", // pink-500
      },
    ];

    // No "Lexi VIP" waitlist slide while VIP promos are off (SHOW_VIP_PROMOS in utils/vars)
    const slides = SHOW_VIP_PROMOS
      ? baseSlides
      : baseSlides.filter((slide) => slide.id !== 3);

    // Lexi Pro users should never see the "Upgrade to Lexi Pro" slide
    if (isLexiPro) {
      return slides.filter((slide) => slide.id !== 2);
    }

    return slides;
  }, [isLexiPro, waitlistStatus.PLATINUM]);

  // One slide (e.g. Lexi Pro members now the VIP slide is off): no arrows, dots, swipe or autoplay.
  const hasMultipleSlides = bannerSlides.length > 1;

  // If the available slides change (e.g., hiding the upgrade slide), keep the index valid.
  useEffect(() => {
    if (currentSlide >= bannerSlides.length) {
      setCurrentSlide(0);
    }
  }, [bannerSlides.length, currentSlide]);

  const nextSlide = () => {
    setCurrentSlide((prev) =>
      prev === bannerSlides.length - 1 ? 0 : prev + 1,
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? bannerSlides.length - 1 : prev - 1,
    );
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    // Reset autoplay timer when manually changing slides
    if (isAutoPlaying) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      startAutoPlay();
    }
  };

  const startAutoPlay = () => {
    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, 7500);
  };

  const pauseAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying((prev) => !prev);
  };

  // Pauses autoplay when user interacts with carousel
  const handleUserInteraction = () => {
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
    }
  };

  // Drag/swipe handlers
  const handleDragStart = (e) => {
    if (!hasMultipleSlides) return;
    const clientX = e.type.includes("mouse") ? e.clientX : e.touches[0].clientX;
    setIsDragging(true);
    setDragStartX(clientX);
    handleUserInteraction();
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;

    const clientX = e.type.includes("mouse") ? e.clientX : e.touches[0].clientX;
    const diff = clientX - dragStartX;

    // Calculate percentage moved relative to container width
    const containerWidth = carouselRef.current?.offsetWidth || 1;
    const percentMoved = (diff / containerWidth) * 100;

    setDragOffset(percentMoved);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);

    // Determine if the drag was significant enough to change slides
    // (typically 20% of width is a good threshold)
    if (Math.abs(dragOffset) > 20) {
      if (dragOffset > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
    }

    // Reset drag offset
    setDragOffset(0);
  };

  useEffect(() => {
    if (isAutoPlaying && hasMultipleSlides) {
      startAutoPlay();
    } else {
      pauseAutoPlay();
    }

    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isAutoPlaying, bannerSlides.length]);

  // Add event listeners for document-level mouse events
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        handleDragMove(e);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        handleDragEnd();
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleMouseMove, { passive: false });
    document.addEventListener("touchend", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, dragStartX]);

  return (
    <div
      className="carousel-banner relative w-full overflow-hidden"
      ref={carouselRef}
    >
      <div
        className={`flex ${!isDragging ? "transition-transform duration-500 ease-in-out" : ""}`}
        style={{
          transform: `translateX(calc(-${currentSlide * 100}% + ${dragOffset}%))`,
          cursor: !hasMultipleSlides ? "default" : isDragging ? "grabbing" : "grab",
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        {bannerSlides.map((slide) => (
          <div
            key={slide.id}
            className="w-full flex-shrink-0 py-6 px-4 flex flex-col items-center justify-center text-white"
            style={{
              background: `linear-gradient(to bottom, ${slide.gradientFrom}, ${slide.gradientTo})`,
            }}
          >
            <h2 className="text-xl font-bold tracking-wider mb-2">
              {slide.title}
            </h2>
            
            {slide.description && Array.isArray(slide.description) && (
              <ul className="text-sm mb-4 text-center text-white">
                {slide.description.map((description, index) => (
                  <li key={index} className="text-sm mb-1 text-center text-white">
                    {description}
                  </li>
                ))}
              </ul>
            )}
            
            
            {slide.cta && slide.ctaAction && (
              <button
                onClick={slide.ctaAction}
                disabled={
                  (slide.isWaitlistButton && waitlistStatus.checking) ||
                  waitlistStatus.joining === slide.waitlistTier ||
                  (slide.isWaitlistButton && waitlistStatus[slide.waitlistTier])
                }
                className={`
                  bg-white text-sm font-bold py-2 px-4 rounded-full text-gray-800 
                  hover:bg-opacity-90 transition-all flex items-center gap-2
                  ${
                    slide.isWaitlistButton && waitlistStatus[slide.waitlistTier]
                      ? "bg-green-100 text-green-800 border-2 border-green-300"
                    : "hover:bg-opacity-90"
                  }
                  ${
                    (slide.isWaitlistButton && waitlistStatus.checking) ||
                    waitlistStatus.joining === slide.waitlistTier
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }
                `}
                data-gtm-element-id={`header_banner_button-${slide.id}`}
              >
                {waitlistStatus.checking && slide.isWaitlistButton ? (
                  <>
                    <Spinner size="small" className="text-gray-600" /> Checking...
                  </>
                ) : waitlistStatus.joining === slide.waitlistTier ? (
                  <>
                    <Spinner size="small" className="text-gray-600" /> Joining...
                  </>
                ) : slide.isWaitlistButton &&
                  waitlistStatus[slide.waitlistTier] ? (
                  <>
                    <Check size={16} className="text-green-600" /> {slide.cta}
                  </>
                ) : (
                  slide.cta
                )}
              </button>
            )}
            
          </div>
        ))}
      </div>

      {/* Arrows and dots only when there is more than one slide */}
      {hasMultipleSlides && (
        <>
          {/* Navigation arrows */}
          <button
            onClick={() => {
              prevSlide();
              handleUserInteraction();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-30 rounded-full p-1 text-white hover:bg-opacity-50 transition-colors"
            aria-label="Previous slide"
            data-gtm-element-id="header_banner-prev_button-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <button
            onClick={() => {
              nextSlide();
              handleUserInteraction();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-30 rounded-full p-1 text-white hover:bg-opacity-50 transition-colors"
            aria-label="Next slide"
            data-gtm-element-id="header_banner-next_button-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>

          {/* Indicator dots */}
          <div className="absolute bottom-2 left-0 right-0 flex justify-center space-x-2">
            {bannerSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  goToSlide(index);
                  handleUserInteraction();
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  currentSlide === index ? "bg-white" : "bg-white bg-opacity-50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
                data-gtm-element-id={`header_banner-dot_button-${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
